<template>
    <v-card flat color="transparent" :disabled="isSpaceResting" class="w-100 align-self-start">
        <v-card-title class="secondary--text">Snapshots</v-card-title>
        <v-card-text>
            <div v-if="fetchingSnapshots">
                <div
                    v-for="item in [
                        { id: 'c1', opacity: 1 },
                        { id: 'c2', opacity: 0.75 },
                        { id: 'c3', opacity: 0.5 }
                    ]"
                    :key="item.id">
                    <div :style="{ opacity: item.opacity }">
                        <v-skeleton-loader type="list-item-three-line" class="mx-auto" />
                    </div>
                </div>
            </div>
            <div v-else>
                <div class="d-flex align-center mb-4">
                    <SnapshotCreateButton v-if="isDevelopment && (isSpaceAdmin || isInstanceEditor)" />
                    <v-menu offset-x nudge-top="10">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" color="secondary" text class="mr-2">
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense nav>
                            <entity-delete-dialog entity-type="snapshot" :snapshotData="selected" @deleting="selected = []">
                                <template v-slot:activator="{ attrs, open }">
                                    <v-list-item v-bind="attrs" :disabled="selected.length === 0 || (isInstanceArchived && !isSpaceAdmin)" @click.stop="open">
                                        <v-list-item-icon>
                                            <v-icon
                                                :color="selected.length === 0 || (isInstanceArchived && !isSpaceAdmin) ? undefined : 'error'"
                                                :class="selected.length === 0 || (isInstanceArchived && !isSpaceAdmin) ? 'text--disabled' : ''">
                                                mdi-trash-can-outline
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title
                                            :class="[
                                                selected.length === 0 || (isInstanceArchived && !isSpaceAdmin) ? 'text--disabled' : 'error--text',
                                                'mr-5'
                                            ]">
                                            delete selected
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </entity-delete-dialog>
                        </v-list>
                    </v-menu>

                    <v-spacer></v-spacer>
                    <v-text-field
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        placeholder="Search for a snapshot..."
                        v-model="search"
                        clearable
                        class="mr-2">
                        <template v-slot:append>
                            <v-menu offset-y :close-on-content-click="false">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                        <v-icon left>mdi-tune-variant</v-icon>
                                        <span>Filters</span>
                                    </v-btn>
                                </template>
                                <v-list nav dense>
                                    <div v-for="(item, index) in Object.values(snapshotCheckboxBinders)" :key="index">
                                        <v-sheet rounded class="py-2 px-4 my-1">
                                            <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item.status">
                                                <template v-slot:label>
                                                    <div class="d-flex align-center">
                                                        <v-icon :color="item.color" left small>mdi-circle</v-icon>
                                                        <div class="d-flex flex-column">
                                                            <span>{{ item.text }}</span>
                                                            <span class="caption">{{ item.description }}</span>
                                                        </div>
                                                    </div>
                                                </template>
                                            </v-switch>
                                        </v-sheet>
                                    </div>
                                </v-list>
                            </v-menu>
                        </template>
                    </v-text-field>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn
                                icon
                                :loading="fetchingSnapshots"
                                v-on="on"
                                @click="$store.dispatch('instanceStore/fetchInstanceSnapshots', $route.params.iid)">
                                <v-icon>refresh</v-icon>
                            </v-btn>
                        </template>
                        <span>Refresh snapshots</span>
                    </v-tooltip>
                </div>

                <v-data-table
                    show-select
                    :search="search"
                    item-key="snid"
                    :headers="headers"
                    v-model="selected"
                    :options="tableOptions"
                    :items="filteredSnapshots"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }">
                    <template v-slot:top="{ pagination, options, updateOptions }">
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </template>
                    <template v-slot:no-data>
                        <p class="mt-6"><b>No snapshots found.</b></p>
                        <p class="mb-6">
                            Snapshots are immutables states of previous work that allow you to reproduce results and restore files, applications, and data.
                            <br />
                            Check the
                            <a href="https://docs.nuvolos.com/how-to-guides/common-workflows/snapshots/create-a-snapshot" target="_blank">documentation here</a>
                            for information on how to create a snapshot.
                        </p>
                    </template>
                    <template v-slot:[`item.type`]="{ item }">
                        <v-icon :color="getSnapshotIndicator(item).color">{{ getSnapshotIndicator(item).icon }}</v-icon>
                    </template>
                    <template v-slot:[`item.long_id`]="{ item }">
                        <v-card flat color="transparent" class="my-1" style="width: 30vw">
                            <v-list-item
                                two-line
                                class="rounded overflow-hidden"
                                :to="{
                                    name: 'snapshot-overview',
                                    params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: item.snid }
                                }">
                                <v-list-item-content>
                                    <v-list-item-title>
                                        <b>{{ item.long_id }}</b>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ item.description ? item.description : 'no description available' }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-icon right color="primary" class="showOnHover">mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-card>
                    </template>
                    <template v-slot:[`item.snapshot_timestamp`]="{ item }">
                        {{ item.snapshot_timestamp | dateTimeToHuman }}
                    </template>
                    <template v-slot:[`item.fhl_total_bytes`]="{ item }">
                        {{ humanFileSize(item.fhl_total_bytes) }}
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-btn small v-on="on" icon>
                                    <v-icon small :color="item.filesystem_prefix ? 'success' : 'info'">
                                        {{ getSnapshotStatusIcon(item) }}
                                    </v-icon>
                                </v-btn>
                            </template>
                            <div>
                                {{ getSnapshotStatusDescription(item) }}
                            </div>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10" v-if="isInstanceEditor">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <snapshot-restore-dialog
                                    v-if="!isDistributedInstance && !isSpaceInAnyRestStateExceptPreResting && !isSpaceArchived && !isSpaceRested"
                                    :snapshotData="item"
                                    :isRestoreToCurrentInstance="true">
                                    <template v-slot:activator="{ attrs, open }">
                                        <v-list-item v-bind="attrs" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">restore</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Restore to current instance</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </snapshot-restore-dialog>
                                <snapshot-restore-dialog
                                    v-if="!isDistributedInstance && !isSpaceArchived && !isSpaceRested"
                                    :snapshotData="item"
                                    :isRestoreToCurrentInstance="false">
                                    <template v-slot:activator="{ attrs, open }">
                                        <v-list-item v-bind="attrs" @click.stop="open">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">group</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Restore to another instance</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </snapshot-restore-dialog>
                                <v-divider
                                    v-if="
                                        (!isDistributedInstance && !isSpaceInAnyRestStateExceptPreResting && !isSpaceArchived && !isSpaceRested) ||
                                        (!isDistributedInstance && !isSpaceArchived && !isSpaceRested)
                                    "
                                    class="my-2"></v-divider>
                                <entity-delete-dialog entity-type="snapshot" :snapshotData="[item]" @deleting="selected = []">
                                    <template v-slot:activator="{ attrs, open }">
                                        <v-list-item v-bind="attrs" :disabled="isInstanceArchived && !isSpaceAdmin" @click.stop="open" e2e-snapshot-delete>
                                            <v-list-item-icon>
                                                <v-icon
                                                    :color="isInstanceArchived && !isSpaceAdmin ? null : 'error'"
                                                    :class="isInstanceArchived && !isSpaceAdmin ? 'text--disabled' : ''">
                                                    mdi-trash-can-outline
                                                </v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title :class="[isInstanceArchived && !isSpaceAdmin ? 'text--disabled' : 'error--text', 'mr-5']">
                                                Delete snapshot
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </entity-delete-dialog>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </div>
        </v-card-text>
    </v-card>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import { sortArray, truncateText, humanFileSize } from '@/utils'
import { enumsData } from '@/mixins/enums'
import { snapshotEnums } from '@/mixins/snapshot'

const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const SnapshotRestoreDialog = () => import('@/modules/snapshot/components/TheSnapshotRestore.vue')
const SnapshotCreateButton = () => import('@/components/SnapshotCreateButton.vue')

export default {
    name: 'SnapshotList',
    mixins: [enumsData, snapshotEnums],
    components: { EntityDeleteDialog, SnapshotRestoreDialog, SnapshotCreateButton },
    data() {
        return {
            search: '',
            headers: [
                { text: 'Type', value: 'type', sortable: false, width: '30px' },
                { text: 'Create date', value: 'snapshot_timestamp' },
                { text: 'Title', value: 'long_id' },
                { text: 'Status', value: 'status' },
                { text: 'Backup size', value: 'fhl_total_bytes' },
                { text: 'Actions', align: 'right', value: 'actions', sortable: false }
            ],
            selected: [],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            }
        }
    },
    methods: {
        snapshotOperation(sourceSnapshotId, operation) {
            const pars = {
                oid: this.$route.params.oid,
                sid: this.$route.params.sid,
                iid: this.$route.params.iid,
                snid: sourceSnapshotId
            }
            if (operation === 'restoreToCurrentInstance') {
                this.$router.push({
                    name: 'snapshot-restore',
                    params: pars
                })
            } else if (operation === 'restoreToAnotherInstance') {
                this.$router.push({
                    name: 'instance-upload-to-development',
                    params: pars
                })
            } else if (operation === 'deleteSnapshot') {
                this.$router.push({
                    name: 'snapshot-delete',
                    params: pars
                })
            }
        },
        truncateText(text, maxLength) {
            return truncateText(text, maxLength)
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        }
    },
    computed: {
        ...mapState('instanceStore', ['fetchingSnapshots']),
        ...mapState(['userMetadata', 'fetchingUserInfo']),
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isDistributedInstance', 'isInstanceArchived']),
        ...mapGetters('snapshotStore', ['nonDevelopmentSnapshots', 'isDevelopment']),
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'isSpaceInAnyRestStateExceptPreResting', 'isSpaceResting', 'isSpaceArchived', 'isSpaceRested']),

        filteredSnapshots() {
            const snapshots = this.nonDevelopmentSnapshots.filter(
                snapshot =>
                    (snapshot.snapshot_type === this.snapshotTypes.QUICK_SNAPSHOT && this.snapshotCheckboxBinders.quickSnapshots.status) ||
                    (snapshot.snapshot_type === this.snapshotTypes.AUTO_SNAPSHOT && this.snapshotCheckboxBinders.autoSnapshots.status) ||
                    (snapshot.snapshot_type === this.snapshotTypes.DETAILED_SNAPSHOT && this.snapshotCheckboxBinders.detailedSnapshots.status) ||
                    (snapshot.snapshot_type === this.snapshotTypes.ARCHIVED_SNAPSHOT && this.snapshotCheckboxBinders.archivedSnapshots.status) ||
                    (snapshot.snapshot_type === this.snapshotTypes.RESTED_SNAPSHOT && this.snapshotCheckboxBinders.restedSnapshots.status)
            )
            return sortArray(snapshots, 'snid', 'descending', false)
        }
    }
}
</script>
